<!--
  Shared conversion-enhancement fields (countdown timer, coupon reveal,
  active-window schedule) used by BOTH the "Announcement bar" and "Popup"
  Sidebar sections. `model` is the reactive bar/popup object itself — the
  inputs below mutate `model.countdown`/`model.coupon`/`model.schedule` in
  place (same pattern Sidebar already uses for `bar.colors.bg` etc.), so no
  v-model/emit round-trip is needed here; the parent's `emitBar`/`emitPopup`
  spread the whole object (including these sub-objects) into the save
  payload.

  `fieldPrefix` ("bar" or "popup") namespaces the `data-widget-field`
  attributes so Vitest can scope a query to one section without matching
  the other (mirrors the existing `data-bar-field`/`data-popup-field`
  convention on the goal-specific inputs).
-->
<template>
  <div>
    <div class="border-t border-gray-100 pt-3 space-y-3">
      <span class="form-label">Countdown timer</span>
      <label class="flex items-center justify-between gap-2">
        <span class="text-sm text-gray-800">Enable countdown</span>
        <input
          v-model="model.countdown.enabled"
          type="checkbox"
          class="form-checkbox"
          :data-widget-field="`${fieldPrefix}-countdown_enabled`">
      </label>

      <div v-if="model.countdown.enabled" class="space-y-3">
        <label class="flex items-center justify-between gap-2">
          <span class="text-sm text-gray-800">Mode</span>
          <select
            v-model="model.countdown.mode"
            class="form-select"
            :data-widget-field="`${fieldPrefix}-countdown_mode`">
            <option value="fixed">Fixed end date</option>
            <option value="session">Duration per visitor</option>
          </select>
        </label>

        <label
          v-if="model.countdown.mode === 'session'"
          class="flex items-center justify-between gap-2">
          <span class="form-label">Duration (minutes)</span>
          <input
            v-model.number="model.countdown.duration_minutes"
            type="number"
            min="1"
            class="form-input w-20"
            :data-widget-field="`${fieldPrefix}-countdown_duration_minutes`">
        </label>
        <label v-else class="block">
          <span class="form-label">Ends at</span>
          <input
            v-model="countdownEndsAt"
            type="datetime-local"
            class="form-input w-full mt-1"
            :data-widget-field="`${fieldPrefix}-countdown_ends_at`">
        </label>

        <label class="block">
          <span class="form-label">Label</span>
          <input
            v-model="model.countdown.label"
            type="text"
            placeholder="Offer ends in:"
            class="form-input w-full mt-1"
            :data-widget-field="`${fieldPrefix}-countdown_label`">
        </label>
      </div>
    </div>

    <div v-if="goal === 'email'" class="border-t border-gray-100 pt-3 space-y-3">
      <span class="form-label">Coupon</span>
      <label class="block">
        <span class="form-label">Code</span>
        <input
          v-model="model.coupon.code"
          type="text"
          placeholder="SAVE10"
          class="form-input w-full mt-1"
          :data-widget-field="`${fieldPrefix}-coupon_code`">
      </label>
      <label class="block">
        <span class="form-label">Reveal text</span>
        <input
          v-model="model.coupon.reveal_text"
          type="text"
          placeholder="Use this code at checkout"
          class="form-input w-full mt-1"
          :data-widget-field="`${fieldPrefix}-coupon_reveal_text`">
      </label>
    </div>

    <div class="border-t border-gray-100 pt-3 space-y-3">
      <span class="form-label">Active window</span>
      <label class="block">
        <span class="form-label">Start showing at</span>
        <input
          v-model="scheduleStartAt"
          type="datetime-local"
          class="form-input w-full mt-1"
          :data-widget-field="`${fieldPrefix}-schedule_start_at`">
      </label>
      <label class="block">
        <span class="form-label">Stop showing at</span>
        <input
          v-model="scheduleEndAt"
          type="datetime-local"
          class="form-input w-full mt-1"
          :data-widget-field="`${fieldPrefix}-schedule_end_at`">
      </label>
      <span class="form-helper">Leave blank for no schedule limit.</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

// The reactive bar/popup object, bound with `v-model` by the parent — must
// already carry `countdown`/`coupon`/`schedule` sub-objects (Sidebar's
// barDefaults/popupDefaults seed these so the bindings above never hit
// `undefined`). Nested mutations flow straight back to the parent's reactive
// object (same reference); the parent's emitBar/emitPopup then spread them
// into the save payload. defineModel keeps this lint-clean (no prop mutation).
const model = defineModel({ type: Object, required: true })

defineProps({
  // 'announce' | 'link' | 'email' | 'call' — gates the coupon fields.
  goal: { type: String, default: '' },
  fieldPrefix: { type: String, required: true }
})

// `<input type="datetime-local">` only speaks local, seconds-less
// `YYYY-MM-DDTHH:MM` — it can neither display nor round-trip a stored UTC
// ISO8601 instant. These two helpers bridge that gap so the model always
// holds a full ISO8601 string (what Website#sanitize_widget_iso8601 expects)
// while the input only ever sees/produces its native local shape.
function isoToLocalInputValue(iso) {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  // Shift the instant by the browser's own tz offset, then format with
  // toISOString() (which always renders in UTC) — the net effect is the
  // *local* wall-clock digits in the shape <input> requires.
  const shifted = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000)
  return shifted.toISOString().slice(0, 16)
}

function localInputValueToIso(localValue) {
  if (!localValue) return ''
  const parsed = new Date(localValue)
  if (Number.isNaN(parsed.getTime())) return ''
  // No tz designator on `localValue` → the JS Date parser reads it as the
  // owner's local wall clock, so toISOString() below already yields the
  // correct UTC instant.
  return parsed.toISOString()
}

// Computed get/set proxy so the model always carries a full ISO8601 string
// (surviving both the save-payload emit and Website's server-side parser)
// while the datetime-local input only ever sees its native local shape.
function dateTimeLocalProxy(getStored, setStored) {
  return computed({
    get: () => isoToLocalInputValue(getStored()),
    set: (localValue) => setStored(localInputValueToIso(localValue))
  })
}

const countdownEndsAt = dateTimeLocalProxy(
  () => model.value.countdown.ends_at,
  (value) => { model.value.countdown.ends_at = value }
)
const scheduleStartAt = dateTimeLocalProxy(
  () => model.value.schedule.start_at,
  (value) => { model.value.schedule.start_at = value }
)
const scheduleEndAt = dateTimeLocalProxy(
  () => model.value.schedule.end_at,
  (value) => { model.value.schedule.end_at = value }
)
</script>
